/**
 * Fetch a single notification_events row for activity log drill-down.
 * Returns message, context, channels, and delivery timestamps/errors.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const QUERY_KEY = 'notification-event-detail';

export interface NotificationEventDetail {
  id: string;
  property_id: string;
  property_name: string;
  event_kind: string | null;
  severity: string;
  title: string;
  message: string;
  context: Record<string, unknown> | null;
  outcome: string;
  suppression_reason: string | null;
  audience_type: string;
  channels: string[] | null;
  delivered_email_at: string | null;
  delivered_sms_at: string | null;
  delivered_push_at: string | null;
  email_error_text: string | null;
  sms_error_text: string | null;
  push_error_text: string | null;
  created_at: string;
}

async function fetchDetail(eventId: string | null): Promise<NotificationEventDetail | null> {
  if (!eventId) return null;
  const { data, error } = await supabase.rpc('get_notification_event_detail', {
    p_event_id: eventId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  const ch = row.channels;
  return {
    id: row.id,
    property_id: row.property_id,
    property_name: row.property_name,
    event_kind: row.event_kind,
    severity: row.severity,
    title: row.title,
    message: row.message ?? '',
    context: row.context ?? null,
    outcome: row.outcome ?? 'sent',
    suppression_reason: row.suppression_reason,
    audience_type: row.audience_type ?? 'installer',
    channels: Array.isArray(ch) ? ch : ch ? [ch] : null,
    delivered_email_at: row.delivered_email_at,
    delivered_sms_at: row.delivered_sms_at,
    delivered_push_at: row.delivered_push_at,
    email_error_text: row.email_error_text,
    sms_error_text: row.sms_error_text,
    push_error_text: row.push_error_text,
    created_at: row.created_at,
  };
}

export function useNotificationEventDetail(eventId: string | null) {
  const query = useQuery({
    queryKey: [QUERY_KEY, eventId],
    queryFn: () => fetchDetail(eventId),
    enabled: Boolean(eventId),
  });
  return {
    detail: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
